import { Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  LeaderboardService,
  SessionActivityEvent,
} from '../../services/leaderboard.service';
import { SessionService } from '../../services/session.service';
import {
  SessionResult,
  SessionPlayer,
  LapTime,
  Stint,
} from '@spectrace/shared';
import { Subscription } from 'rxjs';
import { MiniLeaderboardComponent } from '../mini-leaderboard/mini-leaderboard.component';
import { EditSessionModalComponent } from '../edit-session-modal/edit-session-modal.component';
import { AuthService, User } from '../../services/auth.service';
import { ApiService } from '../../services/api.service';

@Component({
  selector: 'app-current-session',
  standalone: true,
  imports: [CommonModule, MiniLeaderboardComponent, EditSessionModalComponent],
  templateUrl: './current-session.component.html',
  styleUrls: ['./current-session.component.css'],
})
export class CurrentSessionComponent implements OnInit, OnDestroy {
  activeSession: SessionResult | null = null;
  sessionId: string | null = null;
  allLaps: LapTime[] = [];
  remainingLaps = 0;
  totalLapsNeeded = 0;
  stints: Stint[] = [];
  stintsCompleted = 0;
  selectedDriverIds: string[] = [];
  successMessage = 'Session ID has been copied to clipboard!';
  showSuccessMessage = false;
  showEditSessionModal = false;
  currentTime = new Date();
  currentUser: User | null = null;
  sessionActivities: SessionActivityEvent[] = [];

  private subscriptions: Subscription[] = [];
  private timerInterval: any;
  private snapshotRefreshInterval: any;
  private allSessionActivities: SessionActivityEvent[] = [];
  @ViewChild('activityLogScroller')
  private activityLogScroller?: ElementRef<HTMLDivElement>;

  constructor(
    private leaderboardService: LeaderboardService,
    private sessionService: SessionService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private apiService: ApiService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.authService.currentUser$.subscribe((user) => {
        this.currentUser = user;
      }),
    );

    // Get session ID from route parameter
    this.route.paramMap.subscribe((params) => {
      this.stopSnapshotRefresh();
      this.sessionId = params.get('id');
      this.syncVisibleSessionActivities();
      if (this.sessionId) {
        this.loadSessionSnapshot(this.sessionId);
        this.startSnapshotRefresh(this.sessionId);
      }
    });

    // Subscribe to active session
    this.subscriptions.push(
      this.sessionService.activeSession$.subscribe((session) => {
        if (!session) {
          if (!this.sessionId) {
            this.activeSession = null;
          }
          return;
        }

        if (this.sessionId && session.id !== this.sessionId) {
          return;
        }

        this.setSession(session);
        this.loadStints();
      }),
    );

    // Subscribe to entries to get current session's laps
    this.subscriptions.push(
      this.leaderboardService.entries$.subscribe((entries) => {
        if (!this.activeSession) {
          return;
        }

        const exactEntry =
          entries.find((e) => e.id === this.activeSession!.id) || null;
        if (!exactEntry) {
          return;
        }

        this.setSession(exactEntry);
      }),
    );

    this.subscriptions.push(
      this.leaderboardService.sessionActivities$.subscribe((activities) => {
        this.allSessionActivities = activities || [];
        this.syncVisibleSessionActivities();
      }),
    );

    this.timerInterval = setInterval(() => {
      this.currentTime = new Date();
    }, 30000);
  }

  copyMessage(text?: string) {
    if (text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.showSuccessMessage = true;
          setTimeout(() => {
            this.showSuccessMessage = false;
          }, 2000);
        })
        .catch((e) => console.log(e));
    }
  }

  private loadStints(): void {
    this.sessionService.getStints(this.sessionId || undefined).subscribe({
      next: (response) => {
        this.stints = response.stints || [];
      },
      error: (error) => {
        console.error('Failed to load stints:', error);
        this.stints = [];
      },
    });
  }

  get displayedLaps(): LapTime[] {
    if (this.selectedDriverIds.length === 0) {
      return this.allLaps;
    }

    const selectedDrivers = new Set(this.selectedDriverIds);
    return this.allLaps.filter((lap) => {
      const driverId = this.getDriverFilterKeyForLap(lap);
      return driverId ? selectedDrivers.has(driverId) : false;
    });
  }

  get displayedSectorIndexes(): number[] {
    const maxSectorCount = this.displayedLaps.reduce(
      (max, lap) => Math.max(max, lap.sectors?.length || 0),
      0,
    );

    return Array.from({ length: maxSectorCount }, (_value, index) => index);
  }

  getLapDriverName(lap: LapTime): string {
    if (lap.playerId) {
      const player = this.activeSession?.sessionPlayers?.find(
        (sessionPlayer) => sessionPlayer.id === lap.playerId,
      );
      if (player?.displayName) {
        return player.displayName;
      }
    }

    if (lap.playerName) {
      return lap.playerName;
    }

    if (lap.stintId) {
      const stint = this.stints.find(
        (sessionStint) => sessionStint.id === lap.stintId,
      );
      if (stint?.playerName) {
        return stint.playerName;
      }
    }

    return 'Unknown Driver';
  }

  onSelectedDriverIdsChange(selectedDriverIds: string[]): void {
    this.selectedDriverIds = selectedDriverIds;
  }

  /**
   * Get the display lap number for a lap.
   * Always shows absolute lap number.
   */
  getDisplayLapNumber(lap: LapTime): number {
    return lap.lapNumber;
  }

  canEditSession(): boolean {
    return !!(
      this.canManageSession &&
      this.activeSession &&
      this.activeSession.allLaps.length === 0
    );
  }

  openEditSessionModal(): void {
    this.showEditSessionModal = true;
  }

  closeEditSessionModal(): void {
    this.leaderboardService.refreshLeaderboard();
    this.showEditSessionModal = false;
  }

  onSessionUpdated(): void {
    this.closeEditSessionModal();
  }

  endSession(): void {
    if (!this.canManageSession && this.sessionId) {
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: `/session/${this.sessionId}` },
      });
      return;
    }

    if (this.activeSession?.status !== 'active') {
      this.router.navigate(['/sessions']);
      return;
    }

    const recordedLaps = this.activeSession?.allLaps?.length ?? 0;
    if (recordedLaps === 0) {
      const shouldDiscard = window.confirm(
        'This session has no lap data yet. Ending it now will discard it and it will not appear in Completed sessions. End session anyway?',
      );
      if (!shouldDiscard) {
        return;
      }
    }

    const endedSessionId = this.activeSession?.id || this.sessionId || null;
    this.sessionService.endSession().subscribe({
      next: () => {
        if (endedSessionId) {
          this.router.navigate(['/race', endedSessionId]);
          return;
        }
        this.router.navigate(['/sessions']);
      },
      error: (err) => {
        console.error('Error ending session:', err);
      },
    });
  }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.stopSnapshotRefresh();
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  get sessionStateLabel(): 'Live' | 'Scheduled' | 'Completed' {
    if (!this.activeSession) {
      return 'Scheduled';
    }
    if (this.activeSession.status === 'completed') {
      return 'Completed';
    }
    if (this.hasSessionConcluded) {
      return 'Completed';
    }
    const now = this.currentTime.getTime();
    const start = new Date(this.activeSession.sessionDetails.startTime).getTime();
    return now < start ? 'Scheduled' : 'Live';
  }

  get sessionStateClass(): string {
    if (this.sessionStateLabel === 'Live') {
      return 'active';
    }
    if (this.sessionStateLabel === 'Completed') {
      return 'completed';
    }
    return 'scheduled';
  }

  get isSessionScheduled(): boolean {
    return this.sessionStateLabel === 'Scheduled';
  }

  get hasSessionConcluded(): boolean {
    if (!this.activeSession) {
      return false;
    }

    if (this.activeSession.status === 'completed') {
      return true;
    }

    if (this.activeSession.sessionDetails.endTime) {
      return (
        new Date(this.activeSession.sessionDetails.endTime).getTime() <=
        this.currentTime.getTime()
      );
    }

    return false;
  }

  get sessionVisibilityLabel(): string {
    return this.activeSession?.isPublic ? 'Public' : 'Team Members Only';
  }

  get sessionVisibilityClass(): string {
    return this.activeSession?.isPublic ? 'public' : 'private';
  }

  get hasTeamBranding(): boolean {
    return !!(
      this.activeSession &&
      !this.activeSession.isPublic &&
      (this.activeSession.teamName || this.activeSession.teamLogoUrl)
    );
  }

  get teamDisplayName(): string {
    return this.activeSession?.teamName || 'Team Session';
  }

  get teamLogoUrl(): string | null {
    return this.activeSession?.teamLogoUrl || null;
  }

  get teamInitial(): string {
    const label = (this.teamDisplayName || '').trim();
    return label ? label.charAt(0).toUpperCase() : 'T';
  }

  get sessionJoinId(): string {
    return this.activeSession?.id || this.sessionId || '';
  }

  get telemetryAllowedCars(): string {
    const allowedCars = this.activeSession?.allowedCarClasses || [];

    return allowedCars.length > 0
      ? allowedCars.join(', ')
      : 'the allowed cars for this session';
  }

  get telemetryTrackName(): string {
    return (
      this.activeSession?.sessionDetails?.trackName ||
      this.activeSession?.trackName ||
      'the selected track'
    );
  }

  get telemetryLapsPerDriver(): number {
    return this.activeSession?.sessionDetails?.lapsPerDriver || 10;
  }

  get isAuthenticated(): boolean {
    return this.authService.isAuthenticated();
  }

  get canManageSession(): boolean {
    return !!(
      this.currentUser &&
      this.activeSession &&
      this.currentUser.id === this.activeSession.userId
    );
  }

  loginToJoinSession(): void {
    if (!this.sessionId) {
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: '/sessions' },
      });
      return;
    }

    this.router.navigate(['/login'], {
      queryParams: { returnUrl: `/session/${this.sessionId}` },
    });
  }

  private mapSessionToActiveView(session: SessionResult): SessionResult {
    const keepExistingLaps =
      !!this.activeSession &&
      this.activeSession.id === session.id &&
      (session.allLaps || []).length === 0 &&
      (this.activeSession.allLaps || []).length > 0;

    const merged = keepExistingLaps
      ? {
          ...session,
          allLaps: this.activeSession?.allLaps || [],
          bestLap: this.activeSession?.bestLap || session.bestLap,
          lapsCompleted:
            this.activeSession?.lapsCompleted ?? session.lapsCompleted,
        }
      : session;

    return {
      ...merged,
      teamId: merged.teamId || '',
      sessionPlayers: merged.sessionPlayers || [],
      activeStints: merged.activeStints || [],
      allLaps: merged.allLaps || [],
      lapsCompleted: merged.lapsCompleted ?? (merged.allLaps || []).length,
    };
  }

  private setSession(session: SessionResult): void {
    this.activeSession = this.mapSessionToActiveView(session);
    this.updateLapsList();
    this.updatelapsRemainingString();
  }

  private startSnapshotRefresh(sessionId: string): void {
    this.snapshotRefreshInterval = setInterval(() => {
      this.loadSessionSnapshot(sessionId, false);
    }, 5000);
  }

  private stopSnapshotRefresh(): void {
    if (this.snapshotRefreshInterval) {
      clearInterval(this.snapshotRefreshInterval);
      this.snapshotRefreshInterval = null;
    }
  }

  private loadSessionSnapshot(
    sessionId: string,
    navigateOnError: boolean = true,
  ): void {
    const request$ = this.authService.isAuthenticated()
      ? this.apiService.getLeaderboardSessionById(sessionId)
      : this.apiService.getPublicSessionById(sessionId);

    request$.subscribe({
      next: (response) => {
        this.stints = response.stints || [];
        this.setSession(response.session);
        this.leaderboardService.hydrateSessionActivities(
          response.activities || [],
        );
        this.syncSessionParticipants(sessionId);
      },
      error: (error) => {
        console.error('Failed to load session snapshot:', error);
        if (navigateOnError) {
          this.router.navigate(['/sessions']);
        }
      },
    });
  }

  formatSessionDate(date?: Date): string {
    if (!date) return 'Not set';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private updateLapsList(): void {
    // Get all laps from the current session
    this.allLaps = [];

    if (this.activeSession && this.activeSession.allLaps) {
      // Show all laps in chronological order
      this.allLaps = [...this.activeSession.allLaps].sort(
        (a, b) => a.lapNumber - b.lapNumber,
      );
    }
  }

  private updatelapsRemainingString(): void {
    if (!this.activeSession) {
      return;
    }

    const lapsPerDriver = this.activeSession.sessionDetails.lapsPerDriver || 10;
    const totalPlayers = (this.activeSession.sessionPlayers || []).length;
    this.totalLapsNeeded = totalPlayers * lapsPerDriver;

    // Count completed laps
    const completedLaps =
      this.activeSession.lapsCompleted || this.activeSession.allLaps?.length || 0;
    this.remainingLaps = Math.max(0, this.totalLapsNeeded - completedLaps);
    this.stintsCompleted = (this.activeSession.activeStints || []).filter(
      (s: Stint) => s.status === 'completed',
    ).length;
  }

  formatTime(milliseconds: number): string {
    if (milliseconds <= 0) {
      return '--:--.---';
    }

    const totalSeconds = milliseconds / 1000;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const ms = Math.floor(milliseconds % 1000);

    return `${minutes}:${seconds.toString().padStart(2, '0')}.${ms
      .toString()
      .padStart(3, '0')}`;
  }

  formatSectorTime(milliseconds: number): string {
    if (milliseconds <= 0) {
      return '--.-';
    }

    const seconds = (milliseconds / 1000).toFixed(3);
    return seconds;
  }

  getSectorTime(lap: LapTime, sectorIndex: number): string {
    if (!lap.sectors || !lap.sectors[sectorIndex]) {
      return '--.-';
    }
    return this.formatSectorTime(lap.sectors[sectorIndex]);
  }

  getSectorTooltip(lap: LapTime, sectorIndex: number): string {
    const sectorNumber = sectorIndex + 1;
    const sectorTime = this.getSectorTime(lap, sectorIndex);
    const tone = this.getSectorTone(lap, sectorIndex);

    if (sectorTime === '--.-') {
      return `Sector ${sectorNumber}: no data`;
    }

    if (tone === 'session-best') {
      return `Sector ${sectorNumber}: ${sectorTime} (session best)`;
    }

    if (tone === 'personal-best') {
      return `Sector ${sectorNumber}: ${sectorTime} (personal best)`;
    }

    if (tone === 'slower') {
      return `Sector ${sectorNumber}: ${sectorTime}`;
    }

    return `Sector ${sectorNumber}: ${sectorTime}`;
  }

  getSectorChipClass(lap: LapTime, sectorIndex: number): string {
    const tone = this.getSectorTone(lap, sectorIndex);
    switch (tone) {
      case 'session-best':
        return 'sector-chip-session-best';
      case 'personal-best':
        return 'sector-chip-personal-best';
      case 'slower':
        return 'sector-chip-slower';
      default:
        return 'sector-chip-empty';
    }
  }

  getBestLapTime(): string {
    if (!this.activeSession || !this.activeSession.bestLap) {
      return '--:--.---';
    }
    return this.formatTime(this.activeSession.bestLap.lapTime);
  }

  formatDelta(milliseconds: number): string {
    const seconds = (milliseconds / 1000).toFixed(3);
    return seconds;
  }

  trackByActivityId(_index: number, activity: SessionActivityEvent): string {
    return activity.id;
  }

  formatActivityTime(timestamp: string): string {
    const parsed = new Date(timestamp);
    if (Number.isNaN(parsed.getTime())) {
      return '--:--:--';
    }
    return parsed.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  getActivityCategoryTag(activity: SessionActivityEvent): string {
    switch (activity.category) {
      case 'participant':
        return 'JOIN';
      case 'telemetry':
        return 'TELEMETRY';
      case 'lap':
        return 'LAP';
      case 'stint':
        return 'STINT';
      case 'session':
      default:
        return 'SESSION';
    }
  }

  getActivityLevelClass(activity: SessionActivityEvent): string {
    return `level-${activity.level}`;
  }

  isBestLap(lap: LapTime): boolean {
    return !!(
      this.activeSession?.bestLap &&
      lap.lapNumber === this.activeSession.bestLap.lapNumber
    );
  }

  isNotBestLap(lap: LapTime): boolean {
    return !!(
      this.activeSession?.bestLap &&
      lap.lapNumber !== this.activeSession.bestLap.lapNumber
    );
  }

  isSlowerThanBest(lap: LapTime): boolean {
    return !!(
      this.activeSession?.bestLap &&
      lap.lapTime > this.activeSession.bestLap.lapTime
    );
  }

  getDeltaTime(lap: LapTime): number {
    if (!this.activeSession?.bestLap) {
      return 0;
    }
    return lap.lapTime - this.activeSession.bestLap.lapTime;
  }

  private getDriverFilterKeyForLap(lap: LapTime): string | null {
    if (lap.playerId) {
      return lap.playerId;
    }
    if (lap.playerName) {
      return `name:${lap.playerName}`;
    }
    if (lap.stintId) {
      const stint = this.stints.find(
        (sessionStint) => sessionStint.id === lap.stintId,
      );
      if (stint?.playerId) {
        return stint.playerId;
      }
      if (stint?.playerName) {
        return `name:${stint.playerName}`;
      }
    }
    if ((this.activeSession?.sessionPlayers || []).length === 1) {
      return this.activeSession?.sessionPlayers?.[0]?.id || null;
    }
    return null;
  }

  private getSectorTone(
    lap: LapTime,
    sectorIndex: number,
  ): 'session-best' | 'personal-best' | 'slower' | 'empty' {
    const sectorTime = lap.sectors?.[sectorIndex];
    if (!sectorTime || sectorTime <= 0 || lap.isValid === false) {
      return 'empty';
    }

    const sessionBest = this.getSessionBestSectorTime(sectorIndex);
    if (sessionBest !== null && sectorTime === sessionBest) {
      return 'session-best';
    }

    const driverBest = this.getDriverBestSectorTime(lap, sectorIndex);
    if (driverBest !== null && sectorTime === driverBest) {
      return 'personal-best';
    }

    return 'slower';
  }

  private getSessionBestSectorTime(sectorIndex: number): number | null {
    const sectorTimes = this.allLaps
      .filter((lap) => lap.isValid !== false)
      .map((lap) => lap.sectors?.[sectorIndex] || 0)
      .filter((time) => time > 0);

    if (sectorTimes.length === 0) {
      return null;
    }

    return Math.min(...sectorTimes);
  }

  private getDriverBestSectorTime(lap: LapTime, sectorIndex: number): number | null {
    const lapDriverKey = this.getDriverComparisonKey(lap);
    if (!lapDriverKey) {
      return null;
    }

    const sectorTimes = this.allLaps
      .filter((candidateLap) => candidateLap.isValid !== false)
      .filter(
        (candidateLap) => this.getDriverComparisonKey(candidateLap) === lapDriverKey,
      )
      .map((candidateLap) => candidateLap.sectors?.[sectorIndex] || 0)
      .filter((time) => time > 0);

    if (sectorTimes.length === 0) {
      return null;
    }

    return Math.min(...sectorTimes);
  }

  private getDriverComparisonKey(lap: LapTime): string | null {
    if (lap.playerId) {
      return `player:${lap.playerId}`;
    }

    if (lap.playerName) {
      return `name:${lap.playerName}`;
    }

    if (lap.stintId) {
      const stint = this.stints.find(
        (sessionStint) => sessionStint.id === lap.stintId,
      );
      if (stint?.playerId) {
        return `player:${stint.playerId}`;
      }
      if (stint?.playerName) {
        return `name:${stint.playerName}`;
      }
    }

    return null;
  }

  private syncSessionParticipants(sessionId: string): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    this.sessionService.getSessionParticipants(sessionId).subscribe({
      next: (response) => {
        if (!this.activeSession || this.activeSession.id !== sessionId) {
          return;
        }

        const participants = Array.isArray(response?.participants)
          ? response.participants
          : [];

        const mappedPlayers: SessionPlayer[] = participants.map((participant) => ({
          id: participant.id,
          type:
            participant.type === 'member' ||
            participant.type === 'remote'
              ? participant.type
              : 'remote',
          displayName:
            participant.displayName || participant.name || 'Unknown Driver',
          userId: participant.userId || undefined,
          avatarUrl: participant.avatarUrl || undefined,
          carName: participant.carName || undefined,
          carClass: participant.carClass || undefined,
        }));

        if (mappedPlayers.length === 0) {
          return;
        }

        this.activeSession = {
          ...this.activeSession,
          sessionPlayers: mappedPlayers,
        };
        this.updatelapsRemainingString();
      },
      error: () => {
        // Keep snapshot/session payload as fallback when participants endpoint fails.
      },
    });
  }

  private syncVisibleSessionActivities(): void {
    if (!this.sessionId) {
      this.sessionActivities = [];
      return;
    }

    const filtered = this.allSessionActivities.filter(
      (activity) => activity.sessionId === this.sessionId,
    );
    this.sessionActivities = filtered
      .slice()
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      )
      .slice(-200);
    this.scrollActivityLogToBottom();
  }

  private scrollActivityLogToBottom(): void {
    setTimeout(() => {
      const element = this.activityLogScroller?.nativeElement;
      if (!element) {
        return;
      }
      element.scrollTop = element.scrollHeight;
    }, 0);
  }
}
